import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchOrderDetails } from '../../redux/slices/orderSlice';


const OrderConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get data from navigation state
  const { orderId, message, order: locationOrder } = location.state || {};
  
  // Get order from orders slice (when fetched by ID)
  const { orderDetails, loading: orderLoading, error: orderError } = useSelector((state) => state.orders);
  
  // Get order from checkout slice (when coming from checkout finalization)
  const { order: checkoutOrder } = useSelector((state) => state.checkout);
  
  const [isFetching, setIsFetching] = useState(false);

  useEffect(() => {
    // If we have orderId and haven't fetched yet → fetch the order details
    if (orderId && !isFetching && !orderDetails) {
      console.log('🔄 Fetching order details for:', orderId);
      setIsFetching(true);
      dispatch(fetchOrderDetails(orderId));
    }
  }, [orderId, isFetching, orderDetails, dispatch]);

  // Determine which order to display
  // Priority: orderDetails (fetched) > checkoutOrder (from finalization) > locationOrder (passed)
  const order = orderDetails || checkoutOrder || locationOrder;

  console.log('📦 Final order to display:', order);

  // Show loading while fetching
  if (orderId && orderLoading && !order) {
    return (
      <>
        <style>{ocStyles}</style>
        <div className="oc-root">
          <div className="oc-state">
            <span className="oc-state-label">Loading Order</span>
          </div>
        </div>
      </>
    );
  }

  // If we tried to fetch but got an error and have no fallback data
  if (orderId && orderError && !order) {
    return (
      <>
        <style>{ocStyles}</style>
        <div className="oc-root">
          <div className="oc-state">
            <span className="oc-state-label">Something went wrong</span>
            <span className="oc-state-sub">{orderError}</span>
            <button className="oc-btn" onClick={() => navigate('/my-orders')} style={{ marginTop: '20px' }}>
              Go to My Orders
            </button>
          </div>
        </div>
      </>
    );
  }

  // ✅ FIXED: Only redirect if there's truly no order data AND no orderId being fetched
  if (!order && !orderId && !orderLoading) {
    console.log('⚠️ No order data found, redirecting...');
    setTimeout(() => {
      navigate('/my-orders', { replace: true });
    }, 1500);
    
    return (
      <>
        <style>{ocStyles}</style>
        <div className="oc-root">
          <div className="oc-state">
            <span className="oc-state-label">No order data found. Redirecting…</span>
          </div>
        </div>
      </>
    );
  }

  // ✅ ADDED: Show loading if we're waiting for data
  if (!order) {
    return (
      <>
        <style>{ocStyles}</style>
        <div className="oc-root">
          <div className="oc-state">
            <span className="oc-state-label">Loading Order</span>
          </div>
        </div>
      </>
    );
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimatedDelivery = () => {
    const date = new Date(order.createdAt);
    date.setDate(date.getDate() + 7);
    return date.toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPaymentStatusDisplay = () => {
    if (order.paymentStatus) {
      return order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1);
    }
    return order.isPaid ? 'Paid' : 'Unpaid';
  };

  return (
    <>
      <style>{ocStyles}</style>

      <div className="oc-root">

        {/* ── HEADER ── */}
        <div className="oc-header">
          <div>
            <span className="oc-eyebrow">Order Confirmed</span>
            <h1 className="oc-title">Thank <span className="oc-title-bold">You</span></h1>
          </div>
          <Link to="/my-orders" className="oc-back-link">← My Orders</Link>
        </div>

        <div className="oc-rule-wrap"><hr className="oc-hr" /></div>

        {/* ── SUCCESS BANNER ── */}
        <div className="oc-banner">
          <div className="oc-banner-icon">
            <svg width="22" height="22" fill="none" stroke="#fff" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="oc-banner-body">
            <p className="oc-banner-msg">
              {message || 'Your order has been confirmed and is being processed.'}
            </p>
            {order.user?.email && (
              <p className="oc-banner-email">
                Confirmation sent to <strong>{order.user.email}</strong>
              </p>
            )}
          </div>
        </div>

        {/* ── META BAR ── */}
        <div className="oc-meta-bar">
          <div className="oc-meta-left">
            <span className="oc-order-id">#{order._id.slice(-10).toUpperCase()}</span>
            <span className="oc-order-date">{formatDate(order.createdAt)}</span>
          </div>
          <div className="oc-badges">
            <span className="oc-badge oc-badge--processing">{order.status || 'Processing'}</span>
            <span className={`oc-badge ${order.isPaid ? 'oc-badge--paid' : 'oc-badge--unpaid'}`}>
              {getPaymentStatusDisplay()}
            </span>
          </div>
        </div>

        <div className="oc-rule-wrap oc-rule-wrap--sm"><hr className="oc-hr" /></div>

        {/* ── ETA ALERT ── */}
        <div className="oc-alert">
          <div>
            <div className="oc-alert-title">Estimated Delivery</div>
            <p className="oc-alert-text">{estimatedDelivery()}</p>
          </div>
        </div>

        {/* ── INFO GRID ── */}
        <div className="oc-info-grid">

          <div className="oc-info-block">
            <span className="oc-info-heading">Order Summary</span>
            <div className="oc-info-rows">
              <div className="oc-info-row">
                <span className="oc-info-label">Total</span>
                <span className="oc-info-value">₹{order.totalPrice?.toLocaleString()}</span>
              </div>
              <div className="oc-info-row">
                <span className="oc-info-label">Payment</span>
                <span className="oc-info-value">{order.paymentMethod}</span>
              </div>
              <div className="oc-info-row">
                <span className="oc-info-label">Items</span>
                <span className="oc-info-value">{order.orderItems?.length}</span>
              </div>
            </div>
          </div>

          <div className="oc-info-block">
            <span className="oc-info-heading">Shipped To</span>
            <div className="oc-info-rows">
              <div className="oc-info-row">
                <span className="oc-info-label">Name</span>
                <span className="oc-info-value">
                  {order.shippingAddress?.firstName
                    ? [order.shippingAddress.firstName, order.shippingAddress.lastName].filter(Boolean).join(' ')
                    : order.user?.name || 'N/A'}
                </span>
              </div>
              <div className="oc-info-row">
                <span className="oc-info-label">Mobile</span>
                <span className="oc-info-value">
                  {order.shippingAddress?.phone || order.shippingAddress?.mobile || 'N/A'}
                </span>
              </div>
            </div>
          </div>

          <div className="oc-info-block">
            <span className="oc-info-heading">Billed By</span>
            <div className="oc-info-rows">
              <div className="oc-info-row">
                <span className="oc-info-label">Name</span>
                <span className="oc-info-value">{order.user?.name || 'N/A'}</span>
              </div>
              <div className="oc-info-row">
                <span className="oc-info-label">Email</span>
                <span className="oc-info-value oc-info-value--sm">{order.user?.email || 'N/A'}</span>
              </div>
            </div>
          </div>

          <div className="oc-info-block">
            <span className="oc-info-heading">Shipping Address</span>
            <div className="oc-info-rows">
              <div className="oc-info-row">
                <span className="oc-info-label">Address</span>
                <span className="oc-info-value">{order.shippingAddress?.address}</span>
              </div>
              <div className="oc-info-row">
                <span className="oc-info-label">City</span>
                <span className="oc-info-value">{order.shippingAddress?.city}</span>
              </div>
              <div className="oc-info-row">
                <span className="oc-info-label">State</span>
                <span className="oc-info-value">{order.shippingAddress?.state}</span>
              </div>
              <div className="oc-info-row">
                <span className="oc-info-label">Pincode</span>
                <span className="oc-info-value">{order.shippingAddress?.postalCode}</span>
              </div>
            </div>
          </div>

        </div>

        <div className="oc-rule-wrap"><hr className="oc-hr" /></div>

        {/* ── ORDER ITEMS ── */}
        <div className="oc-items-section">
          <div className="oc-items-header">
            <span className="oc-panel-eyebrow">Items</span>
            <h2 className="oc-items-title">
              Order <span className="oc-title-bold">Items</span>
              <span className="oc-items-count"> · {order.orderItems?.length || 0}</span>
            </h2>
          </div>

          {/* Desktop table */}
          <div className="oc-table-wrap">
            <table className="oc-table">
              <thead>
                <tr className="oc-thead-row">
                  <th className="oc-th">Product</th>
                  <th className="oc-th">Details</th>
                  <th className="oc-th oc-th--center">Qty</th>
                  <th className="oc-th oc-th--right">Total</th>
                </tr>
              </thead>
              <tbody>
                {order.orderItems?.map((item, idx) => (
                  <tr key={idx} className="oc-item-row">
                    <td className="oc-td">
                      <div className="oc-product-cell">
                        <img src={item.image} alt={item.name} className="oc-product-thumb" />
                        <span className="oc-product-name">{item.name}</span>
                        {item.freeQuantity > 0 && (
                          <span className="oc-free-tag">{item.freeQuantity} Free</span>
                        )}
                      </div>
                    </td>
                    <td className="oc-td">
                      <span className="oc-item-meta">
                        {item.size && `${item.size}`}
                        {item.size && item.color && ' · '}
                        {item.color && `${item.color}`}
                      </span>
                    </td>
                    <td className="oc-td oc-td--center">
                      <span className="oc-item-qty">{item.quantity}</span>
                    </td>
                    <td className="oc-td oc-td--right">
                      <span className="oc-item-total">₹{(item.price * item.quantity)?.toLocaleString()}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="oc-mobile-items">
            {order.orderItems?.map((item, idx) => (
              <div key={idx} className="oc-mobile-item">
                <img src={item.image} alt={item.name} className="oc-mobile-thumb" />
                <div className="oc-mobile-info">
                  <span className="oc-product-name">{item.name}</span>
                  {item.freeQuantity > 0 && (
                    <span className="oc-free-tag">{item.freeQuantity} Free</span>
                  )}
                  {(item.size || item.color) && (
                    <span className="oc-item-meta" style={{ marginTop: '4px' }}>
                      {item.size && item.size}
                      {item.size && item.color && ' · '}
                      {item.color && item.color}
                    </span>
                  )}
                  <span className="oc-item-meta">Qty · {item.quantity}</span>
                </div>
                <span className="oc-item-total">₹{(item.price * item.quantity)?.toLocaleString()}</span>
              </div>
            ))}
          </div>

          {/* Order total */}
          {order.discountTotal > 0 && (
            <div className="oc-discount-block">
              <div className="oc-discount-row">
                <span className="oc-discount-label">Subtotal</span>
                <span className="oc-discount-value">₹{order.subtotal?.toLocaleString()}</span>
              </div>
              <div className="oc-discount-row">
                <span className="oc-discount-label">{order.offer?.name || 'Offer'}</span>
                <span className="oc-discount-value">— ₹{order.discountTotal?.toLocaleString()}</span>
              </div>
            </div>
          )}
          <div className="oc-order-total">
            <span className="oc-total-label">Order Total</span>
            <span className="oc-total-value">₹{order.totalPrice?.toLocaleString()}</span>
          </div>
        </div>

        <div className="oc-rule-wrap" style={{ marginTop: '40px' }}><hr className="oc-hr" /></div>

        {/* ── ACTIONS ── */}
        <div className="oc-actions">
          <button className="oc-btn" onClick={() => navigate('/my-orders')}>Track Orders</button>
          <button className="oc-btn oc-btn--ghost" onClick={() => navigate('/')}>Continue Shopping</button>
        </div>

        {/* ── HELP ── */}
        <div className="oc-help">
          <span className="oc-help-heading">Need Help?</span>
          <p className="oc-help-text">
            Questions about your order? Our support team is here.
          </p>
          <div className="oc-help-links">
            <Link to="/contact" className="oc-help-link">Contact Support</Link>
            <button
              className="oc-help-link"
              onClick={() => window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })}
            >
              Additional Links
            </button>
          </div>
        </div>

      </div>
    </>
  );
};

/* ── STYLES ── */
const ocStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Exo+2:wght@200;300;400;500;600&display=swap');
  .oc-mobile-items { display: none; }

  @media (max-width: 768px) {
    .oc-table-wrap   { display: none; }
    .oc-mobile-items { display: flex; }
  }

  /* ── ROOT ── */
  .oc-root {
    background: #f8f8f8;
    min-height: 100vh;
    font-family: 'Exo 2', sans-serif;
    padding: 64px 80px 96px;
    max-width: 1400px;
    margin: 0 auto;
    box-sizing: border-box;
  }

  /* ── STATE ── */
  .oc-state {
    display: flex; flex-direction: column;
    align-items: center; gap: 8px;
    padding: 80px 0;
  }
  .oc-state-label {
    font-size: 11px; font-weight: 500;
    letter-spacing: 0.35em; text-transform: uppercase;
    color: black;
  }
  .oc-state-sub {
    font-size: 10px; font-weight: 300;
    letter-spacing: 0.15em; color: black;
  }

  /* ── HEADER ── */
  .oc-header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    margin-bottom: 32px;
    gap: 16px;
  }

  .oc-eyebrow {
    display: flex; align-items: center; gap: 12px;
    font-size: 9px; font-weight: 400;
    letter-spacing: 0.5em; text-transform: uppercase;
    color: black; margin-bottom: 12px;
  }
  .oc-eyebrow::before {
    content: '';
    width: 24px; height: 1px;
    background: black;
    display: block; flex-shrink: 0;
  }

  .oc-title {
    font-size: clamp(28px, 4vw, 52px);
    font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase;
    line-height: 1; margin: 0;
  }
  .oc-title-bold { font-weight: 600; }

  .oc-back-link {
    font-size: 9px; font-weight: 400;
    letter-spacing: 0.3em; text-transform: uppercase;
    color: black; text-decoration: none;
    padding-bottom: 4px;
    border-bottom: 1px solid black;
    transition: border-color 0.2s;
    white-space: nowrap;
    align-self: flex-end;
  }
  .oc-back-link:hover { border-color: #000; }

  /* ── RULE ── */
  .oc-rule-wrap { margin-bottom: 32px; }
  .oc-rule-wrap--sm { margin-bottom: 24px; }
  .oc-hr { border: none; border-top: 1px solid black; margin: 0; }

  /* ── BANNER ── */
  .oc-banner {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 24px 28px;
    background: #000;
    margin-bottom: 28px;
    flex-wrap: wrap;
  }

  .oc-banner-icon {
    width: 40px; height: 40px;
    border: 1px solid rgba(255,255,255,0.2);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
  }

  .oc-banner-body { flex: 1; }

  .oc-banner-msg {
    font-size: 12px; font-weight: 300;
    letter-spacing: 0.06em; color: #fff;
    line-height: 1.7; margin: 0 0 6px;
  }

  .oc-banner-email {
    font-size: 10px; font-weight: 300;
    letter-spacing: 0.1em; text-transform: uppercase;
    color: rgba(255,255,255,0.45);
    margin: 0;
  }
  .oc-banner-email strong { font-weight: 500; color: rgba(255,255,255,0.75); }

  /* ── META BAR ── */
  .oc-meta-bar {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
    flex-wrap: wrap;
  }

  .oc-meta-left { display: flex; flex-direction: column; gap: 4px; }

  .oc-order-id {
    font-size: 13px; font-weight: 500;
    letter-spacing: 0.2em; color: #000;
    font-variant-numeric: tabular-nums;
  }

  .oc-order-date {
    font-size: 10px; font-weight: 300;
    letter-spacing: 0.1em; color: rgba(0,0,0,0.5);
  }

  .oc-badges { display: flex; gap: 6px; flex-wrap: wrap; }

  /* ── BADGES ── */
  .oc-badge {
    font-family: 'Exo 2', sans-serif;
    font-size: 7px; font-weight: 600;
    letter-spacing: 0.3em; text-transform: uppercase;
    padding: 4px 10px; display: inline-block;
  }
  .oc-badge--processing { background: transparent; color: black; border: 1px solid black; }
  .oc-badge--paid       { background: #000; color: #fff; }
  .oc-badge--unpaid     { background: transparent; color: black; border: 1px solid black; }

  /* ── ALERT (ETA) ── */
  .oc-alert {
    padding: 18px 20px;
    margin-bottom: 32px;
    border-left: 3px solid #000;
    background: rgba(0,0,0,0.02);
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }

  .oc-alert-title {
    font-size: 9px; font-weight: 600;
    letter-spacing: 0.35em; text-transform: uppercase;
    color: #000; margin-bottom: 6px;
  }

  .oc-alert-text {
    font-size: 12px; font-weight: 300;
    letter-spacing: 0.06em; color: black;
    line-height: 1.7; margin: 0;
  }

  /* ── INFO GRID ── */
  .oc-info-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 2px;
    margin-bottom: 40px;
  }

  .oc-info-block {
    padding: 24px;
    background: #f8f8f8;
    border: 1px solid black;
  }

  .oc-info-heading {
    display: block;
    font-size: 8px; font-weight: 500;
    letter-spacing: 0.45em; text-transform: uppercase;
    color: black; margin-bottom: 16px;
  }

  .oc-info-rows { display: flex; flex-direction: column; gap: 0; }

  .oc-info-row {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    padding: 8px 0;
    border-bottom: 1px solid black;
  }
  .oc-info-row:last-child { border-bottom: none; }

  .oc-info-label {
    font-size: 8px; font-weight: 400;
    letter-spacing: 0.3em; text-transform: uppercase;
    color: black; flex-shrink: 0;
  }

  .oc-info-value {
    font-size: 11px; font-weight: 300;
    letter-spacing: 0.06em; color: #000;
    text-align: right;
  }
  .oc-info-value--sm { font-size: 10px; }

  /* ── ITEMS SECTION ── */
  .oc-items-section { margin-top: 0; }

  .oc-items-header { margin-bottom: 24px; }

  .oc-panel-eyebrow {
    display: flex; align-items: center; gap: 10px;
    font-size: 8px; font-weight: 400;
    letter-spacing: 0.45em; text-transform: uppercase;
    color: black; margin-bottom: 10px;
  }
  .oc-panel-eyebrow::before {
    content: '';
    width: 18px; height: 1px;
    background: black;
    display: block; flex-shrink: 0;
  }

  .oc-items-title {
    font-size: clamp(20px, 2.5vw, 32px);
    font-weight: 200; color: #000;
    letter-spacing: 0.08em; text-transform: uppercase;
    line-height: 1; margin: 0;
  }

  .oc-items-count {
    font-weight: 300;
    color: black;
    font-size: 0.75em;
  }

  /* ── TABLE ── */
  .oc-table-wrap { overflow-x: auto; margin-bottom: 0; }

  .oc-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Exo 2', sans-serif;
  }

  .oc-thead-row { border-bottom: 1px solid black; }

  .oc-th {
    font-size: 8px; font-weight: 500;
    letter-spacing: 0.4em; text-transform: uppercase;
    color: black;
    padding: 0 16px 14px 0;
    text-align: left; white-space: nowrap;
  }
  .oc-th--right  { text-align: right; padding-right: 0; }
  .oc-th--center { text-align: center; }

  .oc-item-row { border-bottom: 1px solid black; }
  .oc-item-row:last-child { border-bottom: none; }

  .oc-td {
    padding: 18px 16px 18px 0;
    vertical-align: middle;
  }
  .oc-td--right  { text-align: right; padding-right: 0; }
  .oc-td--center { text-align: center; }

  .oc-product-cell {
    display: flex; align-items: center; gap: 14px;
  }

  .oc-product-thumb {
    width: 60px; height: 72px;
    object-fit: cover; flex-shrink: 0;
  }

  .oc-product-name {
    font-size: 10px; font-weight: 500;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: #000; line-height: 1.4;
  }

  .oc-item-meta {
    display: block;
    font-size: 9px; font-weight: 300;
    letter-spacing: 0.18em; text-transform: uppercase;
    color: black;
  }

  .oc-item-qty {
    font-size: 12px; font-weight: 400;
    letter-spacing: 0.1em; color: #000;
  }

  .oc-item-total {
    font-size: 13px; font-weight: 500;
    letter-spacing: 0.06em; color: #000;
  }

  /* ── MOBILE ITEMS ── */
  .oc-mobile-items {
    flex-direction: column; gap: 0;
  }

  .oc-mobile-item {
    display: flex;
    align-items: flex-start;
    gap: 14px;
    padding: 18px 0;
    border-bottom: 1px solid black;
  }
  .oc-mobile-item:last-child { border-bottom: none; }

  .oc-mobile-thumb {
    width: 68px; height: 82px;
    object-fit: cover; flex-shrink: 0;
  }

  .oc-mobile-info {
    flex: 1;
    display: flex; flex-direction: column; gap: 4px;
    min-width: 0;
  }

  /* ── ORDER TOTAL ── */
  .oc-free-tag { display: inline-block; background: #000; color: #fff; font-size: 7px; font-weight: 600; letter-spacing: 0.25em; text-transform: uppercase; padding: 3px 6px; margin-top: 2px; align-self: flex-start; }
  .oc-discount-block { margin-top: 20px; display: flex; flex-direction: column; gap: 8px; }
  .oc-discount-row { display: flex; justify-content: space-between; align-items: baseline; }
  .oc-discount-label { font-size: 8px; font-weight: 400; letter-spacing: 0.3em; text-transform: uppercase; color: black; }
  .oc-discount-value { font-size: 11px; font-weight: 400; letter-spacing: 0.1em; color: black; }
  .oc-order-total {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 20px 0 0;
    border-top: 1px solid black;
    margin-top: 16px;
  }

  .oc-total-label {
    font-size: 9px; font-weight: 500;
    letter-spacing: 0.4em; text-transform: uppercase;
    color: black;
  }

  .oc-total-value {
    font-size: 22px; font-weight: 300;
    letter-spacing: 0.04em; color: #000;
  }

  /* ── ACTIONS ── */
  .oc-actions {
    display: flex;
    gap: 8px;
    margin-bottom: 24px;
    flex-wrap: wrap;
  }

  .oc-btn {
    flex: 1;
    background: #000; color: #fff;
    border: 1px solid #000;
    font-family: 'Exo 2', sans-serif;
    font-size: 8px; font-weight: 600;
    letter-spacing: 0.35em; text-transform: uppercase;
    padding: 14px 24px;
    cursor: pointer;
    transition: background 0.2s, color 0.2s;
    min-width: 160px;
  }
  .oc-btn:hover { background: #222; }

  .oc-btn--ghost {
    background: transparent; color: #000;
  }
  .oc-btn--ghost:hover { background: rgba(0,0,0,0.05); }

  /* ── HELP ── */
  .oc-help {
    padding: 20px 24px;
    border: 1px solid black;
    background: #f8f8f8;
  }

  .oc-help-heading {
    display: block;
    font-size: 8px; font-weight: 600;
    letter-spacing: 0.4em; text-transform: uppercase;
    color: #000; margin-bottom: 8px;
  }

  .oc-help-text {
    font-size: 11px; font-weight: 300;
    letter-spacing: 0.05em; color: black;
    line-height: 1.7; margin: 0 0 12px;
  }

  .oc-help-links {
    display: flex; gap: 24px; flex-wrap: wrap;
  }

  .oc-help-link {
    font-size: 8px; font-weight: 500;
    letter-spacing: 0.3em; text-transform: uppercase;
    color: #000; text-decoration: none;
    background: none; border: none;
    padding: 0; cursor: pointer;
    padding-bottom: 2px;
    border-bottom: 1px solid black;
    transition: border-color 0.2s;
  }
  .oc-help-link:hover { border-color: #000; }

  /* ── RESPONSIVE ── */
  @media (max-width: 1024px) {
    .oc-root { padding: 48px 40px 80px; }
    .oc-info-grid { grid-template-columns: 1fr 1fr; }
  }

  @media (max-width: 768px) {
    .oc-root { padding: 40px 20px 64px; }
    .oc-header { flex-direction: column; align-items: flex-start; gap: 16px; }
    .oc-meta-bar { flex-direction: column; align-items: flex-start; }
    .oc-info-grid { grid-template-columns: 1fr; gap: 8px; }
    .oc-rule-wrap { margin-bottom: 24px; }
    .oc-actions { flex-direction: column; }
    .oc-btn { min-width: unset; }
  }

  @media (max-width: 360px) {
    .oc-root { padding: 32px 14px 48px; }
  }
`;

export default OrderConfirmationPage;
